import type { AudienceProfileSnapshotV1 } from "./audience-profile.ts";
import type { CampaignBriefV1, CampaignContentSlotV1 } from "./campaign.ts";
import type { EffectiveAudienceContextV1 } from "./effective-audience-context.ts";
import type {
  SocialPostCopyGenerationTaskRequestV2,
  SocialPostGenerationTaskRequestV3,
  SocialPostSourceBriefV1,
} from "./generated-content.ts";

const POST_TYPE_LABELS = {
  feed_portrait: "Instagram Feed Portrait",
  feed_square: "Instagram Feed Square",
  story: "Instagram Story",
  carousel_portrait: "Instagram Carousel Portrait",
  carousel_square: "Instagram Carousel Square",
  tiktok_portrait: "TikTok Portrait",
  tiktok_carousel_portrait: "TikTok Carousel Portrait",
} as const;

const formatBoolean = (value: boolean): string => (value ? "yes" : "no");

const formatList = (values: string[]): string => (values.length > 0 ? values.join(", ") : "none");

export const formatSocialCreativeBriefPromptLines = (
  slot: Pick<CampaignContentSlotV1, "socialCreativeBrief">,
): string[] => {
  const brief = slot.socialCreativeBrief;
  if (!brief) {
    return [];
  }

  if (brief.version === 1) {
    return [
      "Social creative brief:",
      `- Style: ${brief.style}`,
      `- Mechanic: ${brief.mechanic}`,
      `- Tension: ${brief.tension}`,
    ];
  }

  return [
    "Social creative brief:",
    `- Content DNA style: ${brief.contentDnaStyle}`,
    `- Platform-native mechanic: ${brief.platformNativeMechanic}`,
    ...(brief.supportingMechanic ? [`- Supporting mechanic: ${brief.supportingMechanic}`] : []),
    `- Scroll-stopping tension: ${brief.scrollStoppingTension}`,
    `- Comment/share rationale: ${brief.whySomeoneWouldComment}`,
  ];
};

export const buildCampaignRunPrompt = (input: {
  campaignName: string;
  campaignSummary: string;
  goals: string[];
  slot: CampaignContentSlotV1;
  additionalDirection?: string;
}): string => {
  const additionalDirection = input.additionalDirection?.trim();
  const lines = [
    `Create a ${POST_TYPE_LABELS[input.slot.postType]} concept for the campaign "${input.campaignName}".`,
    "",
    "CAMPAIGN CONTEXT:",
    `Campaign strategy: ${input.campaignSummary}`,
    `Campaign goals: ${input.goals.join("; ")}`,
    "",
    "PLANNED POST SLOT:",
    `Planned post concept: ${input.slot.concept}`,
    `Primary objective: ${input.slot.objectiveLabel ?? input.slot.objective}`,
    `Platform: ${input.slot.platform}`,
    `Post type: ${POST_TYPE_LABELS[input.slot.postType]}`,
    `Visual approach: ${input.slot.visualApproach}`,
    ...formatSocialCreativeBriefPromptLines(input.slot),
    `Production mode: ${input.slot.productionMode}`,
    `Required capabilities: ${formatList(input.slot.requiredCapabilities)}`,
    `Requires specific person likeness: ${formatBoolean(input.slot.requiresSpecificPersonLikeness)}`,
    `Requires video: ${formatBoolean(input.slot.requiresVideo)}`,
    `Execution feasibility: ${input.slot.executionFeasibility}`,
  ];

  if (input.slot.audienceSegment) {
    lines.push(`Audience segment: ${input.slot.audienceSegment}`);
  }

  if (input.slot.callToAction) {
    lines.push(`CTA direction: ${input.slot.callToAction}`);
  }

  lines.push(
    "",
    "EXECUTION CONSTRAINTS:",
    "- Treat the planned post slot as the source of truth; do not change the platform, post type, objective, audience, CTA, production mode, visual approach, or required capabilities.",
    "- Match the visual approach and production mode in the generated creative direction.",
    "- Only rely on the listed required capabilities; do not introduce unsupported production dependencies.",
    "- Do not request video, motion, audio, or multi-scene footage unless Requires video is yes.",
    "- Do not request a real person's likeness, celebrity, founder portrait, employee portrait, or influencer likeness unless Requires specific person likeness is yes.",
    "- Keep the concept aligned to the campaign strategy and goals, with the CTA supporting the primary objective.",
  );

  if (additionalDirection) {
    lines.push(
      "",
      "ADDITIONAL DIRECTION (lower priority than campaign context, planned slot, and execution constraints):",
      `Additional direction: ${additionalDirection}`,
      "- Apply this direction only when it does not conflict with the campaign context, planned slot, or execution constraints above.",
    );
  }

  return lines.join("\n");
};

export const buildCampaignScheduledPostPrompt = (input: {
  campaignName: string;
  campaignBrief: CampaignBriefV1;
  slot: CampaignContentSlotV1;
  additionalDirection?: string;
}): string =>
  buildCampaignRunPrompt({
    campaignName: input.campaignName,
    campaignSummary: input.campaignBrief.summary,
    goals: input.campaignBrief.goals.map((goal) => goal.label),
    slot: input.slot,
    additionalDirection: input.additionalDirection,
  });

export const buildCampaignScheduledPostGenerationRequest = (input: {
  campaignName: string;
  campaignBrief: CampaignBriefV1;
  slot: CampaignContentSlotV1;
  audienceProfile?: AudienceProfileSnapshotV1;
  effectiveAudience?: EffectiveAudienceContextV1;
}): SocialPostGenerationTaskRequestV3 => ({
  type: "social_post.request",
  version: 3,
  sourceBrief: {
    version: 1,
    origin: "campaign",
    campaignName: input.campaignName,
    campaignBrief: input.campaignBrief,
    slot: input.slot,
    additionalDirection: null,
  } satisfies SocialPostSourceBriefV1,
  platform: input.slot.platform,
  postType: input.slot.postType,
  variantCount: 1,
  brandPresence: "branded",
  audienceProfile: input.audienceProfile,
  effectiveAudience: input.effectiveAudience,
});

export const buildCampaignScheduledPostCopyGenerationRequest = (input: {
  campaignId: string;
  campaignName: string;
  campaignBrief: CampaignBriefV1;
  scheduledPostId: string;
  visualGenerationTaskId?: string;
  slot: CampaignContentSlotV1;
  audienceProfile?: AudienceProfileSnapshotV1;
  effectiveAudience?: EffectiveAudienceContextV1;
}): SocialPostCopyGenerationTaskRequestV2 => ({
  type: "social_post_copy.request",
  version: 2,
  campaignId: input.campaignId,
  scheduledPostId: input.scheduledPostId,
  ...(input.visualGenerationTaskId ? { visualGenerationTaskId: input.visualGenerationTaskId } : {}),
  sourceBrief: {
    version: 1,
    origin: "campaign",
    campaignId: input.campaignId,
    campaignName: input.campaignName,
    campaignBrief: input.campaignBrief,
    slot: input.slot,
    additionalDirection: null,
  },
  platform: input.slot.platform,
  postType: input.slot.postType,
  brandPresence: "branded",
  audienceProfile: input.audienceProfile,
  effectiveAudience: input.effectiveAudience,
});
